/**
 * In-doc ```yaml audio``` player. Same chrome for production preview and
 * /?test=tts-lesson-audio. Not an audiobook app (no speed / sleep / playlist).
 * Resume / bookmark lives on the document ... menu + pause write, not here.
 */
import { Download, Loader2, Pause, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type AudioFencePlayerProps = {
  voiceId?: string
  currentSeconds: number
  durationSeconds: number
  isPlaying?: boolean
  isGenerating?: boolean
  isStale?: boolean
  error?: string | null
  onTogglePlay?: () => void
  onSeek?: (seconds: number) => void
  onDownload?: () => void
  onRegenerate?: () => void
  className?: string
}

function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function AudioFencePlayer({
  voiceId = 'eve',
  currentSeconds,
  durationSeconds,
  isPlaying = false,
  isGenerating = false,
  isStale = false,
  error = null,
  onTogglePlay,
  onSeek,
  onDownload,
  onRegenerate,
  className,
}: AudioFencePlayerProps) {
  const duration = Math.max(durationSeconds, 1)
  const progress = Math.min(1, currentSeconds / duration)

  if (error) {
    return (
      <div
        className={cn(
          'not-prose my-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5',
          className,
        )}
        data-testid={testIds.audioFencePlayer.error}
      >
        <p className="text-sm text-foreground">Could not generate audio</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{error}</p>
        {onRegenerate ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 h-7 px-2"
            onClick={onRegenerate}
            data-testid={testIds.audioFencePlayer.regenerate}
          >
            Try again
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'not-prose my-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5',
        className,
      )}
      data-testid={testIds.audioFencePlayer.root}
    >
      {isStale ? (
        <div
          className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/60 px-2 py-1.5"
          data-testid={testIds.audioFencePlayer.stale}
        >
          <p className="text-xs text-muted-foreground">
            Lesson text changed. Resume is off until you regenerate.
          </p>
          {onRegenerate ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={onRegenerate}
              data-testid={testIds.audioFencePlayer.regenerate}
            >
              Regenerate
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={isGenerating}
          onClick={onTogglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          data-testid={testIds.audioFencePlayer.play}
        >
          {isGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </Button>

        <div className="min-w-0 flex-1">
          <input
            type="range"
            min={0}
            max={duration}
            step={1}
            value={Math.min(currentSeconds, duration)}
            disabled={isGenerating}
            onChange={(event) => onSeek?.(Number(event.target.value))}
            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-border accent-primary"
            aria-label="Seek"
            data-testid={testIds.audioFencePlayer.seek}
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            <span
              className="text-[11px] tabular-nums text-muted-foreground"
              data-testid={testIds.audioFencePlayer.time}
            >
              {formatClock(currentSeconds)} / {formatClock(durationSeconds)}
            </span>
            <span className="text-[11px] text-muted-foreground">voice: {voiceId}</span>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 px-2"
          disabled={isGenerating}
          onClick={onDownload}
          data-testid={testIds.audioFencePlayer.download}
        >
          <Download className="mr-1 h-3.5 w-3.5" />
          Download
        </Button>
      </div>
      <div
        className="mt-2 h-0.5 overflow-hidden rounded-full bg-border/70"
        aria-hidden
      >
        <div
          className="h-full bg-primary/70"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  )
}
